import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const CAMPAIGN_STATUS = {
  ACTIVE:       { label: 'Ativa',        color: 'bg-green-100 text-green-700 border-green-200' },
  PAUSED:       { label: 'Pausada',      color: 'bg-amber-100 text-amber-700 border-amber-200' },
  DELETED:      { label: 'Deletada',     color: 'bg-red-100 text-red-700 border-red-200' },
  ARCHIVED:     { label: 'Arquivada',    color: 'bg-gray-100 text-gray-600 border-gray-200' },
  IN_PROCESS:   { label: 'Em Criação',   color: 'bg-blue-100 text-blue-700 border-blue-200' },
  WITH_ISSUES:  { label: 'Com Problemas',color: 'bg-red-100 text-red-700 border-red-200' },
};

const OBJECTIVES = [
  { value: 'OUTCOME_AWARENESS',  label: 'Reconhecimento' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engajamento' },
  { value: 'OUTCOME_LEADS',      label: 'Leads' },
  { value: 'OUTCOME_SALES',      label: 'Vendas' },
  { value: 'OUTCOME_TRAFFIC',    label: 'Tráfego' },
  { value: 'OUTCOME_APP_PROMOTION', label: 'Promoção de App' },
];

const objectiveLabel = (objective) => OBJECTIVES.find(item => item.value === objective)?.label || objective || 'Sem objetivo';
const money = (amount, suffix = '') => amount ? `R$ ${(Number(amount) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}${suffix}` : 'Não definido';
const parseMoneyInput = (value) => {
  const normalized = String(value || '').replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};
const maskMoneyInput = (value) => {
  const raw = String(value || '').replace(/[^\d,]/g, '');
  const [integer = '', decimals] = raw.split(',');
  const cleanInteger = integer.replace(/^0+(?=\d)/, '') || (decimals !== undefined ? '0' : '');
  return decimals === undefined ? cleanInteger : `${cleanInteger || '0'},${decimals.slice(0, 2)}`;
};
const creativeFromDetails = (details) => (details?.adSets || []).flatMap(set => set.ads || []).map(ad => ad.creative).find(creative => creative?.thumbnail_url || creative?.image_url) || null;

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.error || detail.message || fallback;
  return fallback;
}

function normalizeInsightsPayload(payload) {
  if (!payload) return { summary: null, rows: [] };
  if (payload.summary) return { summary: payload.summary, rows: payload.rows || [] };
  const rows = Array.isArray(payload) ? payload : (payload.insights || payload.data || []);
  const numeric = (value) => Number.parseFloat(value || 0) || 0;
  const summary = rows.reduce((acc, row) => {
    acc.spend += numeric(row.spend);
    acc.impressions += numeric(row.impressions);
    acc.reach += numeric(row.reach);
    acc.clicks += numeric(row.clicks);
    acc.ctr += numeric(row.ctr);
    acc.cpc += numeric(row.cpc);
    acc.cpm += numeric(row.cpm);
    return acc;
  }, { spend: 0, impressions: 0, reach: 0, clicks: 0, ctr: 0, cpc: 0, cpm: 0, conversions: 0 });
  if (rows.length) {
    summary.ctr /= rows.length;
    summary.cpc /= rows.length;
    summary.cpm /= rows.length;
  }
  return { summary, rows };
}

function platformPath(platform, metaPath, tikTokPath) {
  return platform === 'meta' ? metaPath : tikTokPath;
}

const INSIGHT_PERIODS = [
  { value: 'last_7d', label: 'Últimos 7 dias' },
  { value: 'last_30d', label: 'Últimos 30 dias' },
  { value: 'last_90d', label: 'Últimos 90 dias' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'maximum', label: 'Todo o histórico' },
];

const insightMoney = (value) => `R$ ${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const insightNumber = (value) => Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const periodLabel = (value) => INSIGHT_PERIODS.find(item => item.value === value)?.label || value;

function StatusBadge({ status, map }) {
  const s = map[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${s.color}`}>
      {s.label}
    </span>
  );
}

function CampaignFormModal({ open, onClose, onSaved, api, adAccountId, platform }) {
  const empty = { name: '', objective: '', budgetMode: 'daily', budget: '', specialCategory: 'NONE' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const isMeta = platform === 'meta';
  const platformName = isMeta ? 'Meta' : 'TikTok';

  useEffect(() => {
    if (open) setForm(empty);
  }, [open]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome da campanha'); return; }
    if (!form.objective) { toast.error('Selecione um objetivo de campanha'); return; }
    const budgetValue = parseMoneyInput(form.budget);
    if (!form.budget || budgetValue < 1) { toast.error('O orçamento mínimo é R$ 1,00'); return; }
    setSaving(true);
    try {
      const payload = isMeta
        ? { name: form.name.trim(), objective: form.objective, status: 'PAUSED' }
        : { campaignName: form.name.trim(), objective: form.objective, budgetMode: form.budgetMode === 'daily' ? 'DAILY' : 'LIFETIME', budget: budgetValue, operationStatus: 'DISABLE' };
      if (isMeta) {
        const budgetCents = Math.round(budgetValue * 100);
        payload[form.budgetMode === 'daily' ? 'daily_budget' : 'lifetime_budget'] = budgetCents;
        if (form.specialCategory !== 'NONE') payload.special_ad_categories = [form.specialCategory];
      }
      const response = await api.post(platformPath(platform, `/meta/ad-accounts/${adAccountId}/campaigns`, `/ads/tiktok/ad-accounts/${adAccountId}/campaigns`), payload);
      if (response.data?.warning) toast.warning(response.data.warning);
      else toast.success('Campanha criada como pausada');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(e, 'Erro ao criar campanha'));
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Nova campanha {platformName} Ads</DialogTitle>
          <p className="text-sm text-slate-500">Defina a estratégia e o orçamento inicial. A campanha será criada pausada para revisão.</p>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <div>
            <Label className="text-sm font-semibold">Nome da campanha <span className="text-red-500">*</span></Label>
            <Input className="mt-2" placeholder="Ex.: Black Friday — Leads — São Paulo" value={form.name} onChange={e => set('name', e.target.value)} />
            <p className="text-xs text-slate-400 mt-1">Use um padrão que facilite localizar a campanha depois.</p>
          </div>
          <div>
            <Label className="text-sm font-semibold">Objetivo da campanha <span className="text-red-500">*</span></Label>
            <select className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white"
              value={form.objective} onChange={e => set('objective', e.target.value)}>
              <option value="">Selecione o resultado principal...</option>
              {OBJECTIVES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">O objetivo define a otimização e não poderá ser alterado livremente depois da criação.</p>
          </div>
          <div>
            <Label className="text-sm font-semibold">Orçamento da campanha <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3 mt-2">
              <select className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white" value={form.budgetMode} onChange={e => set('budgetMode', e.target.value)}>
                <option value="daily">Orçamento diário</option>
                <option value="lifetime">Orçamento total</option>
              </select>
              <div className="relative"><span className="absolute left-3 top-2.5 text-sm text-slate-400">R$</span><Input inputMode="decimal" className="pl-10" placeholder="5,00" value={form.budget} onChange={e => set('budget', maskMoneyInput(e.target.value))} onBlur={e => { const value = parseMoneyInput(e.target.value); if (value) set('budget', value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })); }} /></div>
            </div>
            <p className="text-xs text-slate-400 mt-1">Informe o valor em reais. O NexoSynX converte R$ 5,00 para 500 centavos antes de enviar à Meta.</p>
          </div>
          {isMeta && <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
            <Label className="text-sm font-semibold">Categoria especial de anúncio</Label>
            <select className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white" value={form.specialCategory} onChange={e => set('specialCategory', e.target.value)}>
              <option value="NONE">Nenhuma</option>
              <option value="EMPLOYMENT">Emprego</option>
              <option value="HOUSING">Habitação</option>
              <option value="CREDIT">Crédito</option>
              <option value="ISSUES_ELECTIONS_POLITICS">Questões sociais, eleições ou política</option>
            </select>
            <p className="text-xs text-slate-500 mt-2">Use uma categoria especial quando a campanha tratar desses temas. A Meta aplicará restrições de segmentação.</p>
          </div>}
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <p className="text-sm font-semibold text-blue-900">Próxima etapa: estrutura de veiculação</p>
            <p className="text-xs text-blue-700 mt-1">A campanha define objetivo e orçamento. Para veicular, ainda será necessário criar um conjunto de anúncios (público, posicionamento e otimização) e um anúncio (criativo e destino).</p>
          </div>
        </div>
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? 'Criando...' : 'Criar campanha pausada'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignInsightsModal({ open, onClose, campaign, api, platform }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('last_30d');

  useEffect(() => {
    if (!open || !campaign?.id) return;
    setLoading(true);
    setError('');
    api.get(platformPath(platform, `/meta/campaigns/${campaign.id}/insights`, `/ads/tiktok/campaigns/${campaign.id}/insights`), { params: { date_preset: period, level: 'campaign', time_increment: '1' } })
      .then(res => setInsights(normalizeInsightsPayload(res.data)))
      .catch(e => { setInsights(null); setError(errorText(e, 'Não foi possível carregar os insights.')); })
      .finally(() => setLoading(false));
  }, [open, campaign?.id, period]);

  const summary = insights?.summary || {};
  const rows = (insights?.rows || []).filter(row => row && (row.date_start || row.date_stop));
  const maxSpend = Math.max(...rows.map(row => Number(row.spend || 0)), 1);
  const metrics = [
    ['Investimento', insightMoney(summary.spend), 'text-blue-700'],
    ['Impressões', insightNumber(summary.impressions), 'text-slate-900'],
    ['Alcance', insightNumber(summary.reach), 'text-slate-900'],
    ['Cliques', insightNumber(summary.clicks), 'text-slate-900'],
    ['CTR', `${Number(summary.ctr || 0).toFixed(2)}%`, 'text-violet-700'],
    ['CPC médio', insightMoney(summary.cpc), 'text-emerald-700'],
    ['CPM médio', insightMoney(summary.cpm), 'text-amber-700'],
    ['Conversões', insightNumber(summary.conversions), 'text-rose-700'],
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div><DialogTitle className="text-xl">Performance da campanha</DialogTitle><p className="mt-1 max-w-2xl truncate text-sm text-slate-500">{campaign?.name || ''}</p></div>
            <select value={period} onChange={e => setPeriod(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"><option disabled>Período</option>{INSIGHT_PERIODS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
          </div>
        </DialogHeader>
        {loading ? (
          <div className="py-16 text-center text-slate-500">Carregando dados de performance...</div>
        ) : insights ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3"><div><p className="text-xs font-semibold uppercase tracking-wider text-blue-600">Período analisado</p><p className="mt-1 text-sm font-semibold text-blue-950">{periodLabel(period)}</p></div><span className="text-xs text-blue-700">{rows.length ? `${rows.length} registros diários` : 'Sem entrega registrada'}</span></div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{metrics.map(([label, value, color]) => <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className={`mt-2 text-xl font-bold ${color}`}>{value}</p></div>)}</div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-center justify-between"><div><h3 className="font-semibold text-slate-900">Investimento por dia</h3><p className="text-xs text-slate-500">Distribuição do gasto no período selecionado</p></div><span className="text-xs font-medium text-slate-500">{rows.length} dias</span></div>{rows.length ? <div className="mt-5 flex h-36 items-end gap-1 overflow-x-auto pb-5">{rows.slice(-45).map((row, index) => <div key={`${row.date_start}-${index}`} className="group relative flex h-full min-w-[14px] flex-1 items-end"><div className="w-full rounded-t bg-blue-500 transition-colors group-hover:bg-blue-700" style={{ height: `${Math.max((Number(row.spend || 0) / maxSpend) * 100, 3)}%` }} title={`${row.date_start}: ${insightMoney(row.spend)}`} /></div>)}</div> : <div className="py-12 text-center text-sm text-slate-500">A Meta não retornou registros de entrega para este período.</div>}</div>
            {rows.length > 0 && <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"><div className="border-b border-slate-100 px-5 py-4"><h3 className="font-semibold text-slate-900">Últimos registros</h3><p className="text-xs text-slate-500">Dados diários retornados pela Meta</p></div><div className="max-h-64 overflow-auto"><table className="w-full text-left text-sm"><thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wider text-slate-400"><tr><th className="px-5 py-3">Data</th><th className="px-5 py-3">Investimento</th><th className="px-5 py-3">Impressões</th><th className="px-5 py-3">Cliques</th><th className="px-5 py-3">CTR</th></tr></thead><tbody>{[...rows].reverse().slice(0, 30).map((row, index) => <tr key={`${row.date_start}-${index}`} className="border-t border-slate-100"><td className="px-5 py-3 font-medium text-slate-700">{row.date_start || '—'}</td><td className="px-5 py-3 text-slate-700">{insightMoney(row.spend)}</td><td className="px-5 py-3 text-slate-600">{insightNumber(row.impressions)}</td><td className="px-5 py-3 text-slate-600">{insightNumber(row.clicks)}</td><td className="px-5 py-3 text-slate-600">{Number(row.ctr || 0).toFixed(2)}%</td></tr>)}</tbody></table></div></div>}
          </div>
        ) : (
          <div className="py-16 text-center text-slate-500">{error || 'Nenhum insight disponível'}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CampaignDetailsModal({ open, onClose, campaign, details, loading }) {
  const adSets = details?.adSets || [];
  const ads = adSets.flatMap(adSet => (adSet.ads || []).map(ad => ({ ...ad, adSetName: adSet.name })));
  const firstCreative = ads.find(ad => ad.creative?.thumbnail_url || ad.creative?.image_url)?.creative;
  const insight = normalizeInsightsPayload(details?.insights).summary;
  const metric = (key, fallback = '—') => insight?.[key] ?? fallback;
  const campaignInfo = details?.campaign || campaign || {};
  const budgetLabel = campaignInfo.daily_budget ? `${money(campaignInfo.daily_budget)} / dia` : campaignInfo.lifetime_budget ? `${money(campaignInfo.lifetime_budget)} total` : 'Não definido';

  return <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div><DialogTitle className="text-xl">{campaign?.name || 'Detalhes da campanha'}</DialogTitle><p className="mt-1 text-sm text-slate-500">Visão completa da campanha, veiculação e criativos.</p></div>
          {campaign && <StatusBadge status={campaign.status} map={CAMPAIGN_STATUS} />}
        </div>
      </DialogHeader>
      {loading ? <div className="py-16 text-center text-slate-500">Carregando detalhes da campanha...</div> : <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[['Orçamento', budgetLabel], ['Investimento', insight ? insightMoney(metric('spend', 0)) : 'Sem dados'], ['Impressões', insight ? insightNumber(metric('impressions', 0)) : 'Sem dados'], ['Cliques', insight ? insightNumber(metric('clicks', 0)) : 'Sem dados']].map(([label, value]) => <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-2 truncate text-lg font-bold text-slate-800">{value}</p></div>)}
        </div>
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Objetivo</p><p className="mt-1 text-sm font-semibold text-slate-800">{objectiveLabel(campaignInfo.objective)}</p></div><div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Conta de anúncios</p><p className="mt-1 text-sm font-semibold text-slate-800">{campaignInfo.ad_account_id || '—'}</p></div><div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Moeda / fuso</p><p className="mt-1 text-sm font-semibold text-slate-800">{campaignInfo.currency || 'BRL'} · {campaignInfo.timezone || '—'}</p></div></div>
        <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            {firstCreative ? <>{firstCreative.thumbnail_url || firstCreative.image_url ? <img src={firstCreative.thumbnail_url || firstCreative.image_url} alt="Prévia do criativo" className="h-64 w-full object-cover" /> : <div className="flex h-64 items-center justify-center text-5xl text-slate-300">{firstCreative.media_type === 'reel' ? '▶' : '✦'}</div>}<div className="p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Criativo em destaque</p><p className="mt-1 font-semibold text-slate-800">{firstCreative.media_type === 'reel' ? 'Reel' : firstCreative.media_type === 'video' ? 'Vídeo' : firstCreative.media_type === 'image' ? 'Imagem' : 'Post'}</p>{firstCreative.instagram_permalink_url && <a href={firstCreative.instagram_permalink_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-800">Abrir publicação ↗</a>}</div></> : <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-8 text-center"><span className="text-4xl text-slate-300">✦</span><p className="mt-3 font-semibold text-slate-700">Nenhuma mídia disponível</p><p className="mt-1 text-xs text-slate-500">O Meta não retornou um preview para os anúncios desta campanha.</p></div>}
          </div>
          <div><div className="flex items-center justify-between"><h3 className="font-semibold text-slate-900">Estrutura de veiculação</h3><span className="text-xs text-slate-500">{adSets.length} conjunto(s) · {ads.length} anúncio(s)</span></div><div className="mt-3 space-y-2">{adSets.length ? adSets.map(adSet => <div key={adSet.id} className="rounded-xl border border-slate-200 p-4"><div className="flex items-center justify-between gap-3"><p className="truncate text-sm font-semibold text-slate-800">{adSet.name}</p><StatusBadge status={adSet.status} map={CAMPAIGN_STATUS} /></div><p className="mt-1 text-xs text-slate-500">Otimização: {adSet.optimization_goal || 'Não informado'}</p><div className="mt-3 flex flex-wrap gap-1.5">{(adSet.ads || []).map(ad => <span key={ad.id} className="rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-600">{ad.name || 'Anúncio'}</span>)}</div></div>) : <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Nenhum conjunto de anúncios encontrado.</div>}</div></div>
        </div>
      </div>}
    </DialogContent>
  </Dialog>;
}

export default function MetaCampaigns({ api, platform = 'meta' }) {
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [insightsCampaign, setInsightsCampaign] = useState(null);
  const [adSets, setAdSets] = useState({});
  const [campaignDetails, setCampaignDetails] = useState({});
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [detailsCampaign, setDetailsCampaign] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [synklyBaseUrl, setSynklyBaseUrl] = useState(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/synkly/config');
        if (res.data?.configured) setSynklyBaseUrl(res.data.base_url || null);
      } catch {}
    };
    fetchConfig();
  }, []);

  const fetchMetaAccounts = async () => {
    try {
      if (platform === 'meta') {
        const res = await api.get('/meta/ad-accounts');
        const accounts = res.data?.adAccounts || (Array.isArray(res.data) ? res.data : []);
        setMetaAccounts(accounts.map(account => ({ ...account, status: 'connected' })));
      } else {
        const res = await api.get('/ads/tiktok/accounts');
        setMetaAccounts(res.data?.accounts || []);
      }
    } catch { setMetaAccounts([]); }
  };

  const fetchAdAccounts = async () => {
    try {
      const res = await api.get(platformPath(platform, '/meta/ad-accounts', '/ads/tiktok/ad-accounts'));
      const data = res.data?.adAccounts || (Array.isArray(res.data) ? res.data : []);
      setAdAccounts(data);
    } catch { setAdAccounts([]); }
  };

  const fetchCampaigns = async () => {
    if (!selectedAdAccount) return;
    setCampaignsLoading(true);
    try {
      const res = await api.get(platformPath(platform, `/meta/ad-accounts/${selectedAdAccount}/campaigns`, `/ads/tiktok/ad-accounts/${selectedAdAccount}/campaigns`));
      const data = res.data?.campaigns || (Array.isArray(res.data) ? res.data : []);
      setCampaigns(data);
    } catch { setCampaigns([]); }
    finally { setCampaignsLoading(false); }
  };

  useEffect(() => {
    Promise.all([fetchMetaAccounts(), fetchAdAccounts()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedAdAccount) {
      fetchCampaigns();
      setAdSets({});
      setExpandedCampaigns(new Set());
      setDetailsCampaign(null);
    }
  }, [selectedAdAccount]);

  const fetchAdSets = async (campaignId) => {
    try {
      if (platform === 'meta') {
        const res = await api.get(`/meta/campaigns/${campaignId}/details`);
        setCampaignDetails(prev => ({ ...prev, [campaignId]: res.data }));
        setAdSets(prev => ({ ...prev, [campaignId]: res.data?.adSets || [] }));
      } else {
        const res = await api.get(`/ads/tiktok/campaigns/${campaignId}/ad-sets`);
        setAdSets(prev => ({ ...prev, [campaignId]: res.data?.adSets || (Array.isArray(res.data) ? res.data : []) }));
      }
    } catch {
      setAdSets(prev => ({ ...prev, [campaignId]: [] }));
    }
  };

  const toggleExpand = (campaignId) => {
    setExpandedCampaigns(current => {
      const next = new Set(current);
      if (next.has(campaignId)) next.delete(campaignId); else next.add(campaignId);
      return next;
    });
    if (!adSets[campaignId]) fetchAdSets(campaignId);
  };

  const openDetails = async (campaign) => {
    setDetailsCampaign(campaign);
    if (campaignDetails[campaign.id]) return;
    setDetailsLoading(true);
    await fetchAdSets(campaign.id);
    setDetailsLoading(false);
  };

  const handlePause = async (campaignId) => {
    try { await api.post(platformPath(platform, `/meta/campaigns/${campaignId}/pause`, `/ads/tiktok/campaigns/${campaignId}/pause`)); toast.success('Campanha pausada'); fetchCampaigns(); }
    catch (e) { toast.error(errorText(e, 'Erro ao pausar')); }
  };

  const handleResume = async (campaignId) => {
    try { await api.post(platformPath(platform, `/meta/campaigns/${campaignId}/resume`, `/ads/tiktok/campaigns/${campaignId}/resume`)); toast.success('Campanha retomada'); fetchCampaigns(); }
    catch (e) { toast.error(errorText(e, 'Erro ao retomar')); }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Deseja realmente deletar esta campanha? Esta ação não pode ser desfeita.')) return;
    try { await api.delete(platformPath(platform, `/meta/campaigns/${campaignId}`, `/ads/tiktok/campaigns/${campaignId}`)); toast.success(platform === 'meta' ? 'Campanha deletada' : 'Campanha arquivada'); fetchCampaigns(); }
    catch (e) { toast.error(errorText(e, 'Erro ao deletar')); }
  };

  const hasActiveAccount = metaAccounts.some(a => a.status === 'connected' || a.metadata?.businessId);
  const platformName = platform === 'tiktok' ? 'TikTok' : 'Meta';
  const synklyPanelUrl = synklyBaseUrl || 'https://app.synkly.com';
  const visibleCampaigns = campaigns.filter(c => c.name?.toLowerCase().includes(search.trim().toLowerCase()));
  const campaignStats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'ACTIVE').length,
    paused: campaigns.filter(c => c.status === 'PAUSED').length,
    issues: campaigns.filter(c => c.status === 'WITH_ISSUES').length,
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-8">
      <section className="rounded-2xl bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 px-6 py-7 text-white shadow-sm">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-blue-200"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Sincronizado via Synkly</div>
            <h1 className="mt-2 text-2xl font-bold tracking-tight">Campanhas {platformName} Ads</h1>
            <p className="mt-1 text-sm text-slate-300">Acompanhe, crie e controle suas campanhas em um só lugar.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchMetaAccounts(); fetchAdAccounts(); if (selectedAdAccount) fetchCampaigns(); }} className="border-slate-600 bg-white/5 text-white hover:bg-white/10 hover:text-white">↻ Atualizar</Button>
            <a href={synklyPanelUrl} target="_blank" rel="noopener noreferrer"><Button className="bg-white text-slate-950 hover:bg-slate-100">Abrir Synkly ↗</Button></a>
          </div>
        </div>
      </section>

      {!hasActiveAccount ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6 flex gap-4 items-start">
            <span className="text-xl">⚠️</span><div><p className="font-semibold text-amber-950">Nenhuma conta de anúncio disponível</p><p className="text-sm mt-1 text-amber-800">Conecte a conta, selecione o Business e sincronize as Ad Accounts no Synkly.</p></div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-end">
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Conta de anúncio</Label>
                  <select
                    className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedAdAccount}
                    onChange={e => setSelectedAdAccount(e.target.value)}
                  >
                    <option value="">Selecione uma conta para visualizar campanhas</option>
                    {adAccounts.map(a => (
                      <option key={a.adAccountId || a.id} value={platform === 'meta' ? a.adAccountId : a.id}>
                        {a.name} ({a.currency || ''})
                      </option>
                    ))}
                  </select>
                </div>
                <Button onClick={() => setShowCreate(true)} disabled={!selectedAdAccount} className="bg-blue-600 hover:bg-blue-700 text-white px-5">+ Nova campanha</Button>
              </div>
            </CardContent>
          </Card>

          {selectedAdAccount && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[['Total', campaignStats.total, 'text-slate-900'], ['Ativas', campaignStats.active, 'text-emerald-600'], ['Pausadas', campaignStats.paused, 'text-amber-600'], ['Com problemas', campaignStats.issues, 'text-red-600']].map(([label, value, color]) => <Card key={label} className="border-slate-200"><CardContent className="p-4"><p className="text-xs font-medium text-slate-500">{label}</p><p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p></CardContent></Card>)}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div><h2 className="font-semibold text-slate-900">Campanhas</h2><p className="text-sm text-slate-500">{campaignsLoading ? 'Atualizando campanhas...' : `${visibleCampaigns.length} resultado(s) nesta conta`}</p></div>
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar campanha..." className="w-full sm:w-64" />
              </div>
              {campaignsLoading ? <Card><CardContent className="p-10 text-center text-slate-500">Carregando campanhas...</CardContent></Card> : campaigns.length === 0 ? (
                <Card className="border-dashed border-slate-300"><CardContent className="p-10 text-center"><div className="text-3xl">◎</div><p className="mt-3 font-semibold text-slate-800">Nenhuma campanha encontrada</p><p className="mt-1 text-sm text-slate-500">Crie sua primeira campanha pausada e revise antes de ativá-la.</p><Button onClick={() => setShowCreate(true)} className="mt-4 bg-blue-600">Criar campanha</Button></CardContent></Card>
              ) : visibleCampaigns.length === 0 ? <Card><CardContent className="p-8 text-center text-sm text-slate-500">Nenhuma campanha corresponde à busca.</CardContent></Card> : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                {visibleCampaigns.map(c => {
                  const creative = creativeFromDetails(campaignDetails[c.id]);
                  return (
                  <Card key={c.id} className="group overflow-hidden border-slate-200 bg-white shadow-sm hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg transition-all">
                    <div className={`h-1 ${c.status === 'ACTIVE' ? 'bg-emerald-500' : c.status === 'WITH_ISSUES' ? 'bg-red-500' : c.status === 'PAUSED' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                    <div className="grid lg:grid-cols-[148px_1fr]">
                      <button type="button" onClick={() => openDetails(c)} className="relative min-h-[148px] overflow-hidden bg-slate-100 text-left lg:min-h-full" aria-label={`Visualizar mídia de ${c.name}`}>
                        {creative?.thumbnail_url || creative?.image_url ? <img src={creative.thumbnail_url || creative.image_url} alt="Prévia do criativo" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <div className="flex h-full min-h-[148px] flex-col items-center justify-center gap-2 p-4 text-center text-slate-400"><span className="text-3xl">✦</span><span className="text-[11px] font-medium">Abrir prévia da mídia</span></div>}
                        <span className="absolute bottom-2 left-2 rounded-full bg-slate-950/75 px-2 py-1 text-[10px] font-semibold text-white">{creative ? 'Criativo' : 'Ver mídia'}</span>
                      </button>
                      <CardContent className="p-5">
                      <div className="flex flex-col gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleExpand(c.id)}
                              aria-label="Ver conjuntos de anúncios" className="flex w-7 h-7 items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-transform">
                              <svg className={`w-4 h-4 transition-transform ${expandedCampaigns.has(c.id) ? 'rotate-90' : ''}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            <span className="font-semibold text-slate-900">{c.name}</span>
                            <StatusBadge status={c.status} map={CAMPAIGN_STATUS} />
                          </div>
                          <div className="mt-4 ml-9 grid grid-cols-2 gap-2"><div className="rounded-lg bg-slate-50 px-3 py-2"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Objetivo</p><p className="mt-0.5 truncate text-xs font-semibold text-slate-700">{objectiveLabel(c.objective)}</p></div><div className="rounded-lg bg-slate-50 px-3 py-2"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Orçamento</p><p className="mt-0.5 truncate text-xs font-semibold text-slate-700">{c.daily_budget ? money(c.daily_budget, '/dia') : money(c.lifetime_budget, ' total')}</p></div></div>
                          {creative ? <div className="mt-3 ml-9 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2"><img src={creative.thumbnail_url || creative.image_url} alt="Criativo da campanha" className="h-14 w-20 rounded-lg object-cover" /><div><p className="text-xs font-semibold text-slate-700">Criativo disponível</p><p className="text-[11px] text-slate-500">Abra a visualização para ver a mídia completa</p></div></div> : <div className="mt-3 ml-9 rounded-xl border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-400">Clique em “Visualizar campanha” para carregar a mídia</div>}
                        </div>
                        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                          {c.status === 'ACTIVE' && (
                            <button onClick={() => handlePause(c.id)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">Pausar</button>
                          )}
                          {c.status === 'PAUSED' && (
                            <button onClick={() => handleResume(c.id)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100">Retomar</button>
                          )}
                          <button onClick={() => openDetails(c)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700">Visualizar campanha</button>
                          <button onClick={() => setInsightsCampaign(c)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100">Insights</button>
                          <button onClick={() => handleDelete(c.id)} className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-700">Excluir</button>
                        </div>
                      </div>

                      {expandedCampaigns.has(c.id) && (
                        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Conjuntos de anúncios</p>
                          {(adSets[c.id] || []).length > 0 ? (
                            <div className="space-y-2">
                              {adSets[c.id].map(as => (
                                <div key={as.id} className="bg-white rounded-lg border border-slate-100 p-3 text-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{as.name}</span>
                                    <StatusBadge status={as.status} map={CAMPAIGN_STATUS} />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Otimização: {as.optimization_goal || '—'}
                                  </p>
                                  {(campaignDetails[c.id]?.adSets?.find(item => item.id === as.id)?.ads || []).length > 0 && (
                                    <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Anúncios e criativos</p>
                                      {campaignDetails[c.id].adSets.find(item => item.id === as.id).ads.map(ad => {
                                        const creative = ad.creative || {};
                                        const preview = creative.thumbnail_url || creative.image_url;
                                        return <div key={ad.id} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 p-2.5">
                                          {preview ? <img src={preview} alt="Prévia do criativo" className="h-12 w-12 rounded-md object-cover bg-slate-200" /> : <div className="h-12 w-12 rounded-md bg-slate-200 flex items-center justify-center text-lg">{creative.media_type === 'reel' ? '▶' : '✦'}</div>}
                                          <div className="min-w-0 flex-1"><p className="truncate text-xs font-semibold text-slate-800">{ad.name || 'Anúncio sem nome'}</p><p className="text-[11px] text-slate-500">{creative.media_type === 'reel' ? 'Reel' : creative.media_type === 'video' ? 'Vídeo' : creative.media_type === 'image' ? 'Imagem' : 'Post'} · {ad.status || '—'}</p></div>
                                          {creative.instagram_permalink_url && <a href={creative.instagram_permalink_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-[11px] font-medium text-blue-600 hover:text-blue-800">Ver mídia ↗</a>}
                                        </div>;
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500">Nenhum conjunto de anúncios foi encontrado.</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                    </div>
                  </Card>
                  );
                })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <CampaignFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSaved={fetchCampaigns}
        api={api}
        adAccountId={selectedAdAccount}
        platform={platform}
      />

      <CampaignInsightsModal
        open={!!insightsCampaign}
        onClose={() => setInsightsCampaign(null)}
        campaign={insightsCampaign}
        api={api}
        platform={platform}
      />

      <CampaignDetailsModal
        open={!!detailsCampaign}
        onClose={() => setDetailsCampaign(null)}
        campaign={detailsCampaign}
        details={detailsCampaign ? campaignDetails[detailsCampaign.id] : null}
        loading={detailsLoading}
      />
    </div>
  );
}
